/**
 * AuthGate (TMMAPP-0003). Layout route at the root of the authenticated tree: with no bearer in
 * memory it attempts ONE silent refresh; success renders the outlet (and replays a stashed deep
 * link), failure leaves for centralized login. A runtime session expiry renders the interstitial
 * in place — never a redirect loop.
 */
import { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SessionExpired } from '@/routes/SessionExpired';
import { HiddenPageTitle } from '@/shell/PageHeader';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { useAuth } from './authHooks';
import { getAccessToken } from './authStore';
import { bootRefresh, popDeepLink, redirectToCentralizedLogin } from './silentRefresh';

type GateStatus = 'booting' | 'authed' | 'redirecting';

export function AuthGate() {
  const { t } = useTranslation();
  const { sessionExpired, setAuthed } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<GateStatus>(() => (getAccessToken() ? 'authed' : 'booting'));
  // StrictMode double-invokes the mount effect; the boot refresh is issued once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // A token in memory: the initializer already resolved `'authed'`; nothing to refresh.
    if (getAccessToken()) return;
    const current = `${location.pathname}${location.search}`;
    void bootRefresh().then((ok) => {
      if (ok) {
        setAuthed();
        setStatus('authed');
        const deep = popDeepLink();
        if (deep && deep !== current) void navigate(deep, { replace: true });
      } else {
        setStatus('redirecting');
        redirectToCentralizedLogin(current);
      }
    });
  }, [location.pathname, location.search, navigate, setAuthed]);

  if (sessionExpired) return <SessionExpired />;
  if (status === 'authed') return <Outlet />;

  return (
    <StandaloneFrame>
      <HiddenPageTitle title={t('auth.signingIn')} />
      <Typography variant="body1" role="status">
        {t(status === 'redirecting' ? 'auth.redirecting' : 'common.loading')}
      </Typography>
    </StandaloneFrame>
  );
}
